<template>
  <div>
    <div class="content-top-bottom" v-if="!loading">
      <v-subheader @action="showDiscardPopup = true" :title="headerTitle">
        <v-button
          id="checkout-button"
          class="pv-20"
          :disabled="!isMachineValid"
          @click.prevent="goToCheckOut()"
          primary
        >
          <i class="icon-basket"></i>
          Check out
        </v-button>
      </v-subheader>
      <div class="content-center order-form" id="my-bag-form">
        <div class="container">
          <v-order-submit-machine :freeItemsLeft="freeItemsLeft" :freeItemsTotal="freeItemsTotal" />
          <v-order-submit-price />
          <v-order-submit-pills
            :med-key-type="medKeyType"
            :freeItemsLeft="freeItemsLeft"
            :freeItemsTotal="freeItemsTotal"
          />
        </div>
      </div>
      <v-order-discard-popup v-if="showDiscardPopup" @discard="discardChanges()" @close="showDiscardPopup = false" />
      <v-error-popup
        v-if="saveErrorMessage"
        :title="' '"
        :description="saveErrorMessage"
        @close="saveErrorMessage = null"
        submit-button-text="page.order.submit.delivery.validation.error"
      />
    </div>
    <v-loader v-else />
    <v-order-confirmation-popup
      v-if="$route.query.confirmationStep"
      @close="$router.push({ query: { confirmationStep: undefined } })"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import * as OrderDAO from "@/dao/order-dao";
import { VButton, VLoader } from "shared";
import VSubheader from "@/components/v-subheader";
import VOrderConfirmationPopup from "@/components/order-submit/v-order-confirmation-popup";
import VOrderDiscardPopup from "@/components/order/v-order-discard-popup";
import VOrderSubmitMachine from "./v-order-submit-machine";
import VOrderSubmitPrice from "./v-order-submit-price";
import VOrderSubmitPills from "./v-order-submit-pills";
import VErrorPopup from "@/components/v-error-popup.vue";
import * as ObjectUtils from "@/utils/ObjectUtils";

export default {
  name: "POrderSubmit",
  components: {
    VButton,
    VLoader,
    VSubheader,
    VOrderConfirmationPopup,
    VOrderDiscardPopup,
    VOrderSubmitMachine,
    VOrderSubmitPrice,
    VOrderSubmitPills,
    VErrorPopup,
  },
  props: {
    medKeyType: {
      type: String,
      default: null,
    },
    workListId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      showDiscardPopup: false,
      freeItemsLeft: null,
      freeItemsTotal: null,
      saveErrorMessage: null,
    };
  },
  computed: {
    ...mapGetters("order", [
      "orderListAmount",
      "selectedMachine",
      "referenceNumber",
      "onProductionElements",
      "onHold",
      "formatPrice",
      "basicData",
      "freeContingent",
      "orderList",
      "deliveryTypes",
    ]),
    isMachineValid() {
      return this.basicData.checkoutDisplayMode === "us" ? this.referenceNumber : true;
    },
    headerTitle() {
      return `${this.$t("page.order.submit.header.start")} (${this.orderListAmount} ${this.$t(
        "page.order.submit.header.end"
      )})`;
    },
  },
  methods: {
    ...mapActions("order", ["getMachines", "getBasicData", "getList", "getDeliveryTypes", "getPriceList"]),
    async loadData() {
      this.loading = true;
      await Promise.all([
        this.getMachines(),
        this.getBasicData(),
        this.getPriceList(),
        this.getFreeContingentData(),
        this.getDeliveryTypes(),
      ]);
      await this.getList({ workListId: this.workListId });
      this.loading = false;
    },
    async getFreeContingentData() {
      const { freeItemsLeft, freeItemsTotal } = await OrderDAO.getFreeContingentDetails();
      this.freeItemsLeft = freeItemsLeft;
      this.freeItemsTotal = freeItemsTotal;
    },
    discardChanges() {
      this.$router.push({ name: "myPills" });
    },
    beforeLeaveConfirmation(event) {
      (event || window.event).returnValue = true;
    },
    async goToCheckOut() {
      if (this.isMachineValid) {
        const response = await OrderDAO.getOrdersValidation(
          {
            referenceNumber: this.referenceNumber,
            machineId: ObjectUtils.getProperty(this.selectedMachine, "id", null),
            // TODO: verify if we can get it here
            // deliveryAddressId: this.deliveryAddress.id,
            freeContingent: this.freeContingent,
            orderPositions: this.orderList.map((item) => ({
              id: item.id,
              medKey: item.medKey,
              name: item.name,
              assemblyArticleNumber: item.uvResistant
                ? item.canisterSize === "small"
                  ? item.assemblyArticleNumberSmallUvResistant
                  : item.assemblyArticleNumberLargeUvResistant
                : item.canisterSize === "small"
                ? item.assemblyArticleNumberSmallNonUvResistant
                : item.assemblyArticleNumberLargeNonUvResistant,
              rotorArticleNumber: item.rotorArticleNumber,
              deliveryTypeId: item.deliveryTypeId,
              canisterSize: item.canisterSize,
              uvResistant: item.uvResistant,
              amountOfCanisters: item.amountOfCanisters,
              isOnHold: item.isOnHold,
            })),
          },
          // TODO: verify if we can get it here
          this.purchaseDocument
        );

        if (response.ValidationStatus === "Error") {
          this.saveErrorMessage = response.ValidationMessageTranslationKey;
        } else {
          this.$router.push({ query: { confirmationStep: 1 } });
        }
      }
    },
  },
  async created() {
    await this.loadData();

    window.addEventListener("beforeunload", this.beforeLeaveConfirmation);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.beforeLeaveConfirmation);
  },
  beforeRouteLeave(to, from, next) {
    if (this.showDiscardPopup || this.$route.query.confirmationStep) {
      next(true);
    } else {
      this.showDiscardPopup = true;
      next(false);
    }
  },
};
</script>

<style lang="scss">
.icon-basket {
  background-image: url("~@/assets/icons/icon-basket.svg");
  width: 20px;
  height: 20px;
}
.order-form {
  padding: 88px 0 0 0;
  background: #fbfbfb;
}
</style>
